import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AppProvider, useApp } from "@/context/AppContext";
import SplashScreen from "@/components/SplashScreen";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingSocials from "@/components/FloatingSocials";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import Wishlist from "@/pages/Wishlist";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import MyAccount from "@/pages/MyAccount";
import Signup from "@/pages/account/Signup";
import ForgotPassword from "@/pages/account/ForgotPassword";
import ResetPassword from "@/pages/account/ResetPassword";
import VerifyEmail from "@/pages/account/VerifyEmail";
import OrderTracking from "@/pages/OrderTracking";
import Admin from "@/pages/Admin";
import AdminLogin from "@/pages/AdminLogin";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import ShippingPolicy from "@/pages/ShippingPolicy";
import ReturnPolicy from "@/pages/ReturnPolicy";
import Terms from "@/pages/Terms";
import FAQ from "@/pages/FAQ";

const Protected = ({ children, admin = false }) => {
  const { user, authLoading } = useApp();
  const location = useLocation();
  if (authLoading) return <div className="min-h-screen grid place-items-center label-xs">Loading…</div>;
  if (!user) {
    if (admin) {
      // Admin portal is unlinked from the public site — unauthenticated visits
      // bounce silently to Home. Only /admin/login is a discoverable entry.
      return <Navigate to="/" replace />;
    }
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (admin && !user.is_admin) return <Navigate to="/" replace />;
  return children;
};

const Shell = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order/:orderNo" element={<OrderConfirmation />} />
          <Route path="/wishlist" element={<Protected><Wishlist /></Protected>} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/account/signup" element={<Signup />} />
          <Route path="/account/forgot" element={<ForgotPassword />} />
          <Route path="/account/reset" element={<ResetPassword />} />
          <Route path="/account/verify" element={<VerifyEmail />} />
          <Route path="/track" element={<OrderTracking />} />
          <Route path="/track/:orderNo" element={<OrderTracking />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/account" element={<Protected><MyAccount /></Protected>} />
          <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
          <Route path="*" element={<div className="max-w-6xl mx-auto p-24 text-center"><h1 className="text-6xl">404</h1><p className="mt-4 label-xs">Page not found</p></div>} />
        </Routes>
      </main>
      <Footer />
      <FloatingSocials />
      <Toaster position="bottom-right" richColors />
    </div>
  );
};

export default function App() {
  const [splashDone, setSplashDone] = useState(() => {
    // Show splash once per browser session.
    try { return sessionStorage.getItem("tf_splash_seen") === "1"; } catch { return false; }
  });
  useEffect(() => {
    if (splashDone) {
      try { sessionStorage.setItem("tf_splash_seen", "1"); } catch {}
    }
  }, [splashDone]);
  return (
    <AppProvider>
      {!splashDone && <SplashScreen onDone={() => setSplashDone(true)} />}
      <BrowserRouter>
        <Shell />
      </BrowserRouter>
    </AppProvider>
  );
}
